import React from 'react';
import { PlayIcon, SkipBackIcon, SkipForwardIcon, PauseIcon } from 'lucide-react';
interface SpotifyPreviewProps {
  videos: any[];
  onRemoveVideo: (id: string) => void;
  onUpdateVideo: (id: string, updates: any) => void;
}
export const SpotifyPreview = ({
  videos,
  onRemoveVideo,
  onUpdateVideo
}: SpotifyPreviewProps) => {
  // Only display custom videos with thumbnails
  const customVideos = videos.filter(video => video.isCustom && video.customThumbnail);
  // If no custom videos, show placeholder
  if (customVideos.length === 0) {
    return <div className="flex flex-col items-center justify-center p-8 text-center" data-id="element-248">
        <div className="bg-gray-800 rounded-full p-4 mb-4" data-id="element-249">
          <PlayIcon size={32} className="text-green-500" data-id="element-250" />
        </div>
        <h3 className="text-xl font-medium mb-2" data-id="element-251">No thumbnails yet</h3>
        <p className="text-gray-400 mb-4" data-id="element-252">
          Upload thumbnails to see how they'll appear in Spotify
        </p>
      </div>;
  }
  // Use the first custom video for the preview
  const video = customVideos[0];
  // Use podcast cover image if available, otherwise use the thumbnail
  const coverImage = video.podcastCoverImage || video.customThumbnail;
  return <div className="flex justify-center" data-id="element-253">
      <div className="w-full max-w-sm" data-id="element-254">
        <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl" data-id="element-255">
          <div className="w-[148px] h-[18px] bg-gray-800 top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute" data-id="element-256"></div>
          <div className="h-[46px] w-[3px] bg-gray-800 absolute -left-[17px] top-[124px] rounded-l-lg" data-id="element-257"></div>
          <div className="h-[46px] w-[3px] bg-gray-800 absolute -left-[17px] top-[178px] rounded-l-lg" data-id="element-258"></div>
          <div className="h-[64px] w-[3px] bg-gray-800 absolute -right-[17px] top-[142px] rounded-r-lg" data-id="element-259"></div>
          <div className="rounded-[2rem] overflow-hidden w-full h-full bg-black" data-id="element-260">
            {/* Spotify App UI */}
            <div className="w-full h-full bg-[#121212] flex flex-col" data-id="element-261">
              {/* Status Bar */}
              <div className="h-6 w-full bg-black flex justify-between items-center px-4" data-id="element-262">
                <span className="text-white text-xs" data-id="element-263">9:41</span>
                <div className="flex items-center" data-id="element-264">
                  <span className="text-white text-xs mr-1" data-id="element-265">LTE</span>
                  <span className="text-white text-xs mr-1" data-id="element-266">80%</span>
                </div>
              </div>
              {/* Spotify Header */}
              <div className="p-4 bg-gradient-to-b from-green-900 to-[#121212]" data-id="element-267">
                <div className="flex justify-between items-center" data-id="element-268">
                  <span className="text-white text-xl font-bold" data-id="element-269">
                    Now Playing
                  </span>
                  <span className="text-gray-400 text-sm" data-id="element-270">Spotify</span>
                </div>
              </div>
              {/* Album Art */}
              <div className="px-8 pt-6" data-id="element-271">
                <div className="aspect-square w-full shadow-2xl" data-id="element-272">
                  <div className="w-full h-full relative" data-id="element-273">
                    <img src={coverImage} alt={video.title} className="w-full h-full object-cover" onError={e => {
                    e.currentTarget.src = 'https://via.placeholder.com/500x500?text=Thumbnail+Error';
                  }} data-id="element-274" />
                  </div>
                </div>
              </div>
              {/* Track Info */}
              <div className="px-8 pt-6 flex-1" data-id="element-275">
                <h2 className="text-white text-xl font-bold line-clamp-2" data-id="element-276">
                  {video.title}
                </h2>
                <p className="text-gray-400 text-sm mt-1" data-id="element-277">
                  {video.channelName || 'Unknown Artist'}
                </p>
                {/* Progress Bar */}
                <div className="mt-8" data-id="element-278">
                  <div className="h-1 w-full bg-gray-700 rounded-full" data-id="element-279">
                    <div className="h-1 w-1/3 bg-green-500 rounded-full" data-id="element-280"></div>
                  </div>
                  <div className="flex justify-between mt-2" data-id="element-281">
                    <span className="text-gray-400 text-xs" data-id="element-282">1:23</span>
                    <span className="text-gray-400 text-xs" data-id="element-283">
                      {video.duration || '3:45'}
                    </span>
                  </div>
                </div>
                {/* Controls */}
                <div className="flex justify-center items-center mt-8 space-x-8" data-id="element-284">
                  <button className="text-white opacity-70 hover:opacity-100" data-id="element-285">
                    <SkipBackIcon size={24} data-id="element-286" />
                  </button>
                  <button className="bg-white rounded-full p-3 text-black hover:scale-105 transition-transform" data-id="element-287">
                    <PauseIcon size={24} data-id="element-288" />
                  </button>
                  <button className="text-white opacity-70 hover:opacity-100" data-id="element-289">
                    <SkipForwardIcon size={24} data-id="element-290" />
                  </button>
                </div>
                {/* Bottom Text */}
                <div className="mt-auto mb-6 text-center" data-id="element-291">
                  <p className="text-gray-400 text-xs mt-8" data-id="element-292">
                    This is how your thumbnail will appear in Spotify
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};